import React, { useMemo, useState } from 'react';
import { etiquetaTipoCuenta } from './legalLabels';
import { aniosNacimiento, diasDelMes, esCif, esDniNie, esTelefonoEspana } from './legalValidation';

const MESES = [
  { value: '1', label: 'Enero' },
  { value: '2', label: 'Febrero' },
  { value: '3', label: 'Marzo' },
  { value: '4', label: 'Abril' },
  { value: '5', label: 'Mayo' },
  { value: '6', label: 'Junio' },
  { value: '7', label: 'Julio' },
  { value: '8', label: 'Agosto' },
  { value: '9', label: 'Septiembre' },
  { value: '10', label: 'Octubre' },
  { value: '11', label: 'Noviembre' },
  { value: '12', label: 'Diciembre' },
];

function pad(value) {
  return String(value).padStart(2, '0');
}

function Campo({ label, name, value, onChange, type = 'text', required = false, optional = false }) {
  const texto = optional ? `${label} (opcional)` : required ? `${label} *` : label;
  const Tag = type === 'textarea' ? 'textarea' : 'input';
  return (
    <div className="legal-campo">
      <label htmlFor={name}>{texto}</label>
      <Tag
        id={name}
        name={name}
        type={type === 'textarea' ? undefined : type}
        value={value}
        onChange={onChange}
        required={required}
      />
    </div>
  );
}

function Declaracion({ name, checked, onChange, children }) {
  return (
    <label className="legal-declaracion">
      <input type="checkbox" name={name} checked={checked} onChange={onChange} required />
      <span>{children}</span>
    </label>
  );
}

function SelectFecha({ id, label, value, onChange, children }) {
  return (
    <div className="legal-campo">
      <label htmlFor={id}>{label}</label>
      <select id={id} className="bm-select" value={value} onChange={onChange} required>
        <option value="">Selecciona</option>
        {children}
      </select>
    </div>
  );
}

function payloadDesdeFormulario(formData, tenantType, birthDate) {
  if (tenantType === 'EMPRESA') {
    return {
      ...formData,
      tenantType,
      birthDate,
      fullName: formData.representativeName,
      dniNie: formData.representativeDni,
      email: formData.representativeEmail,
    };
  }
  return { ...formData, tenantType, birthDate };
}

function validar(formData, tenantType, birthDate) {
  const errors = [];
  const dni = tenantType === 'EMPRESA' ? formData.representativeDni : formData.dniNie;
  if (dni && !esDniNie(dni)) {
    errors.push('El DNI/NIE no tiene un formato válido');
  }
  if (formData.phone && !esTelefonoEspana(formData.phone)) {
    errors.push('El teléfono debe ser un número español (+34)');
  }
  if (tenantType === 'PERSONA_FISICA' && !birthDate) {
    errors.push('La fecha de nacimiento es obligatoria para persona física');
  }
  if (tenantType === 'AUTONOMO') {
    if (formData.nifFiscal && !esDniNie(formData.nifFiscal)) {
      errors.push('El NIF fiscal no tiene un formato válido');
    }
    if (!(formData.fiscalAddress || '').trim()) {
      errors.push('El domicilio fiscal es obligatorio para autónomo');
    }
  }
  if (tenantType === 'EMPRESA' && formData.cif && !esCif(formData.cif)) {
    errors.push('El CIF no tiene un formato válido');
  }
  return errors;
}

function resumenFilas(formData, tenantType, birthDate) {
  const filas = [];
  if (tenantType === 'EMPRESA') {
    filas.push(['Representante', formData.representativeName]);
    filas.push(['DNI/NIE', formData.representativeDni]);
    filas.push(['Cargo', formData.representativePosition]);
    filas.push(['Email', formData.representativeEmail]);
    filas.push(['Teléfono', formData.phone]);
    filas.push(['Razón social', formData.companyName]);
    filas.push(['CIF', formData.cif]);
    filas.push(['Domicilio social', formData.registeredAddress]);
    filas.push(['Email corporativo', formData.corporateEmail]);
    return filas;
  }
  filas.push(['Nombre', formData.fullName]);
  filas.push(['DNI/NIE', formData.dniNie]);
  filas.push(['Email', formData.email]);
  filas.push(['Teléfono', formData.phone]);
  if (tenantType === 'PERSONA_FISICA') {
    filas.push(['Fecha de nacimiento', birthDate]);
    filas.push(['Dirección', formData.address]);
  }
  if (tenantType === 'AUTONOMO') {
    filas.push(['NIF fiscal', formData.nifFiscal]);
    filas.push(['Domicilio fiscal', formData.fiscalAddress]);
  }
  return filas.filter(([, valor]) => valor);
}

function TenantProfileForm({ tenantType, onSubmit, loading }) {
  const [formData, setFormData] = useState({
    fullName: '',
    dniNie: '',
    email: '',
    phone: '',
    birthYear: '',
    birthMonth: '',
    birthDay: '',
    address: '',
    personalUseDeclaration: false,
    nifFiscal: '',
    tradeName: '',
    iaeCode: '',
    fiscalAddress: '',
    professionalDeclaration: false,
    companyName: '',
    cif: '',
    registeredAddress: '',
    registryVolume: '',
    registryFolio: '',
    registrySheet: '',
    corporateEmail: '',
    corporatePhone: '',
    representativeName: '',
    representativeDni: '',
    representativePosition: '',
    representativeEmail: '',
    representationDeclaration: false,
  });
  const [errors, setErrors] = useState([]);
  const [confirmando, setConfirmando] = useState(false);

  const anios = useMemo(() => aniosNacimiento(), []);
  const dias = useMemo(() => {
    if (!formData.birthYear || !formData.birthMonth) return 31;
    return diasDelMes(Number(formData.birthYear), Number(formData.birthMonth));
  }, [formData.birthYear, formData.birthMonth]);

  const birthDate = formData.birthYear && formData.birthMonth && formData.birthDay
    ? `${formData.birthYear}-${pad(formData.birthMonth)}-${pad(formData.birthDay)}`
    : '';

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const avisos = validar(formData, tenantType, birthDate);
    if (avisos.length) {
      setErrors(avisos);
      setConfirmando(false);
      return;
    }
    setErrors([]);
    setConfirmando(true);
  };

  const confirmar = () => {
    onSubmit(payloadDesdeFormulario(formData, tenantType, birthDate));
  };

  return (
    <form onSubmit={handleSubmit} className="legal-form">
      <h2>Datos de {etiquetaTipoCuenta(tenantType).toLowerCase()}</h2>
      {errors.length > 0 && (
        <div className="legal-banner-error">{errors.join('. ')}</div>
      )}

      {tenantType !== 'EMPRESA' && (
        <section className="legal-form-seccion">
          <h3>Identidad</h3>
          <div className="legal-form-grid">
            <Campo label="Nombre y apellidos" name="fullName" value={formData.fullName} onChange={handleChange} required />
            <Campo label="DNI/NIE" name="dniNie" value={formData.dniNie} onChange={handleChange} required />
            <Campo label="Email" name="email" type="email" value={formData.email} onChange={handleChange} required />
            <Campo label="Teléfono" name="phone" type="tel" value={formData.phone} onChange={handleChange} required />
          </div>
        </section>
      )}

      {tenantType === 'PERSONA_FISICA' && (
        <section className="legal-form-seccion">
          <h3>Uso personal</h3>
          <div className="legal-form-grid legal-fecha-grid">
            <SelectFecha
              id="birthYear"
              label="Año de nacimiento"
              value={formData.birthYear}
              onChange={(e) => setFormData((prev) => ({ ...prev, birthYear: e.target.value, birthDay: '' }))}
            >
              {anios.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </SelectFecha>
            <SelectFecha
              id="birthMonth"
              label="Mes de nacimiento"
              value={formData.birthMonth}
              onChange={(e) => setFormData((prev) => ({ ...prev, birthMonth: e.target.value, birthDay: '' }))}
            >
              {MESES.map((mes) => (
                <option key={mes.value} value={mes.value}>{mes.label}</option>
              ))}
            </SelectFecha>
            <SelectFecha
              id="birthDay"
              label="Día de nacimiento"
              value={formData.birthDay}
              onChange={(e) => setFormData((prev) => ({ ...prev, birthDay: e.target.value }))}
            >
              {Array.from({ length: dias }, (_, i) => i + 1).map((dia) => (
                <option key={dia} value={dia}>{dia}</option>
              ))}
            </SelectFecha>
            <Campo label="Dirección" name="address" type="textarea" value={formData.address} onChange={handleChange} optional />
          </div>
          <Declaracion name="personalUseDeclaration" checked={formData.personalUseDeclaration} onChange={handleChange}>
            Contrato este servicio para uso propio, sin actuar en el ejercicio de una actividad empresarial o profesional *
          </Declaracion>
        </section>
      )}

      {tenantType === 'AUTONOMO' && (
        <section className="legal-form-seccion">
          <h3>Actividad</h3>
          <div className="legal-form-grid">
            <Campo label="NIF fiscal" name="nifFiscal" value={formData.nifFiscal} onChange={handleChange} required />
            <Campo label="Nombre comercial" name="tradeName" value={formData.tradeName} onChange={handleChange} optional />
            <Campo label="Epígrafe IAE" name="iaeCode" value={formData.iaeCode} onChange={handleChange} optional />
            <Campo label="Domicilio fiscal" name="fiscalAddress" type="textarea" value={formData.fiscalAddress} onChange={handleChange} required />
          </div>
          <Declaracion name="professionalDeclaration" checked={formData.professionalDeclaration} onChange={handleChange}>
            Declaro que actúo en el ejercicio de mi actividad profesional/empresarial *
          </Declaracion>
        </section>
      )}

      {tenantType === 'EMPRESA' && (
        <>
          <section className="legal-form-seccion">
            <h3>Representante</h3>
            <div className="legal-form-grid">
              <Campo label="Nombre y apellidos" name="representativeName" value={formData.representativeName} onChange={handleChange} required />
              <Campo label="DNI/NIE" name="representativeDni" value={formData.representativeDni} onChange={handleChange} required />
              <Campo label="Cargo" name="representativePosition" value={formData.representativePosition} onChange={handleChange} required />
              <Campo label="Email profesional" name="representativeEmail" type="email" value={formData.representativeEmail} onChange={handleChange} required />
              <Campo label="Teléfono" name="phone" type="tel" value={formData.phone} onChange={handleChange} required />
            </div>
            <Declaracion name="representationDeclaration" checked={formData.representationDeclaration} onChange={handleChange}>
              Declaro que ostento poder de representación suficiente para contratar en nombre de
              {formData.companyName ? ` ${formData.companyName}` : ' [razón social]'} y vincular a dicha entidad a los términos aceptados *
            </Declaracion>
          </section>
          <section className="legal-form-seccion">
            <h3>Sociedad</h3>
            <div className="legal-form-grid">
              <Campo label="Razón social" name="companyName" value={formData.companyName} onChange={handleChange} required />
              <Campo label="CIF" name="cif" value={formData.cif} onChange={handleChange} required />
              <Campo label="Domicilio social" name="registeredAddress" type="textarea" value={formData.registeredAddress} onChange={handleChange} required />
              <Campo label="Tomo" name="registryVolume" value={formData.registryVolume} onChange={handleChange} optional />
              <Campo label="Folio" name="registryFolio" value={formData.registryFolio} onChange={handleChange} optional />
              <Campo label="Hoja" name="registrySheet" value={formData.registrySheet} onChange={handleChange} optional />
              <Campo label="Email corporativo" name="corporateEmail" type="email" value={formData.corporateEmail} onChange={handleChange} required />
              <Campo label="Teléfono corporativo" name="corporatePhone" type="tel" value={formData.corporatePhone} onChange={handleChange} optional />
            </div>
          </section>
        </>
      )}

      <button type="submit" className="legal-btn-primary" disabled={loading}>
        {loading ? 'Guardando…' : 'Continuar a la lectura'}
      </button>

      {confirmando && (
        <div className="legal-modal" role="dialog" aria-labelledby="legal-confirm-title">
          <div className="legal-modal-card">
            <h3 id="legal-confirm-title">¿Son correctos estos datos?</h3>
            <dl className="legal-confirm-resumen">
              {resumenFilas(formData, tenantType, birthDate).map(([clave, valor]) => (
                <div key={clave}>
                  <dt>{clave}</dt>
                  <dd>{valor}</dd>
                </div>
              ))}
            </dl>
            <div className="legal-modal-acciones">
              <button type="button" className="legal-btn-secondary" onClick={() => setConfirmando(false)}>
                Volver
              </button>
              <button type="button" className="legal-btn-primary" onClick={confirmar} disabled={loading}>
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
}

export default TenantProfileForm;
